import { useState, useEffect, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import HeroAbout from './components/HeroAbout';

// Lazy-load below-fold sections for code splitting & faster initial render
const KeyFigures = lazy(() => import('./components/KeyFigures'));
const Parcours = lazy(() => import('./components/Parcours'));
const Expertise = lazy(() => import('./components/Expertise'));
const WhyMe = lazy(() => import('./components/WhyMe'));
const CTAAbout = lazy(() => import('./components/CTAAbout'));
const Footer = lazy(() => import('../home/components/Footer'));

function SectionFallback() {
  return (
    <div className="w-full py-16 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-orange-300 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );
}

export default function APropos() {
  const [showNavbar, setShowNavbar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowNavbar(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com';
    const scriptId = 'jsonld-a-propos';
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'À Propos | Usine à Leads',
      description: "Découvrez l'équipe et l'expertise derrière l'Usine à Leads. Notre mission : transformer votre acquisition en machine à rendez-vous qualifiés.",
      url: `${siteUrl}/a-propos`,
      inLanguage: 'fr-FR',
      publisher: {
        '@type': 'Organization',
        name: 'Usine à Leads',
        url: siteUrl,
        logo: 'https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/62fb907743b5e07417bde9cd3a46f134.png',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'À Propos', item: `${siteUrl}/a-propos` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      const s = document.getElementById(scriptId);
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <title>À Propos | Usine à Leads - Expertise en Génération de Leads</title>
      <meta name="description" content="Découvrez l'équipe et l'expertise derrière l'Usine à Leads. Notre mission : transformer votre acquisition en machine à rendez-vous qualifiés en continu." />
      <meta name="keywords" content="à propos usine à leads, expertise génération leads, agence acquisition clients, équipe marketing digital" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/a-propos`} />
      <meta property="og:title" content="À Propos | Usine à Leads - Expertise en Génération de Leads" />
      <meta property="og:description" content="Découvrez l'équipe et l'expertise derrière l'Usine à Leads. Transformer votre acquisition en machine à rendez-vous qualifiés." />
      <meta property="og:url" content={`${import.meta.env.VITE_SITE_URL ?? 'https://usineleads.com'}/a-propos`} />
      <meta property="og:type" content="website" />

      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showNavbar ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="cursor-pointer">
              <img
                src="https://static.readdy.ai/image/857147547f7da499a0fc7d35aaf6d8ec/8eef813465f798e5a4134d50e400907e.png"
                alt="Usine à Leads"
                className="h-12 w-auto"
                loading="eager"
                decoding="async"
                width="160"
                height="48"
              />
            </Link>
            <button
              data-iclosed-link="https://app.iclosed.io/e/usinealead/rdv-45-min-votre-business-votre-besoin"
              data-embed-type="popup"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold text-sm hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Obtenir des rendez-vous
            </button>
          </div>
        </div>
      </nav>

      {/* Hero is above fold — eager, no Suspense */}
      <HeroAbout />

      {/* Below-fold sections — lazy loaded */}
      <Suspense fallback={<SectionFallback />}>
        <KeyFigures />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Parcours />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Expertise />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <WhyMe />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <CTAAbout />
      </Suspense>
      <Suspense fallback={<SectionFallback />}>
        <Footer />
      </Suspense>
    </div>
  );
}
